<template>
  <div ref="loader">
    <div class="w-100" v-bind:class="mainHeaderBgClasses">
      <b-container class="container-lg">
        <b-row class="p-0 pt-2">
          <b-col class="col-9 text-start min-width-0">
            <h5 class="text-truncate">
              <i class="fa-solid fa-chevron-left" @click="goBack"></i>
              {{ statuspagegroupview.statuspagegroup?.name }}
            </h5>
          </b-col>
          <b-col class="col-3 text-end">
            <button class="btn btn-sm" type="button" v-bind:class="mainHeaderRefreshBtnClasses"
                    @click="refreshData">
              <i :class="{'fa-spin': loading}"
                 class="fa-solid fa-arrows-rotate"></i>
            </button>
          </b-col>
        </b-row>
        <b-row class="p-0 pt-2 pb-2">
          <b-col class="col-12">
            <i class="fa-solid fa-server me-1"></i>
            {{ $t('Total hosts monitored') }}: {{ statuspagegroupview.totalHosts }}
          </b-col>
        </b-row>
        <b-row class="p-0 pt-2 pb-2">
          <b-col class="col-12">
            <i class="fa-solid fa-cogs me-1"></i>
            {{ $t('Total services monitored') }}: {{ statuspagegroupview.totalServices }}
          </b-col>
        </b-row>
      </b-container>
    </div>
    <b-container class="container-lg mt-0 p-0 pt-4">
      <b-list-group class="m-2">
        <b-list-group-item>
          <b-row>
            <b-col>
              <b-button-group class="w-100">
                <button :class="{ 'btn btn-primary': selectedTab === 'tab1', 'btn btn-light': selectedTab !== 'tab1' }"
                        type="button"
                        @click="setTab('tab1')">
                  <i class="fa-solid fa-eye"></i>
                  {{ $t('Overview') }}
                </button>
                <button v-if="statuspagegroupview.problems?.length > 0"
                        :class="{ 'btn btn-primary': selectedTab === 'tab2', 'btn btn-light': selectedTab !== 'tab2' }"
                        type="button"
                        @click="setTab('tab2')">
                  <i class="fa-solid fa-info-circle"></i>
                  {{ $t('Details') }}
                </button>
              </b-button-group>
            </b-col>
          </b-row>
        </b-list-group-item>
      </b-list-group>

      <div v-show="selectedTab === 'tab1'" class="m-2 border-0">
        <b-list-group-item>
          <div class="row pt-2">
            <div class="col">
              <small v-if="statuspagegroupview.problems?.length > 0">
                <i class="fa-solid fa-triangle-exclamation text-danger"></i>
                {{ $t('Problems') }}
              </small>
              <small v-else>
                <i class="fa-solid fa-check-circle text-success"></i>
                {{ $t('No problems detected') }}
              </small>
            </div>
          </div>
          <b-list-group class="mt-2">
            <b-list-group-item v-if="statuspagegroupview.problems?.length > 0">
              <Carousel>
                <Slide v-for="problem in statuspagegroupview.problems">
                  <b-card class="w-75 bg-color-lightGray min-h-90">
                    <small>
                      <statuspagegroups-icon :status="problem.cumulatedState"/>

                      <router-link
                          :to="{name: 'statuspagesDetails', params: {id: problem.statuspage.statuspage.id}}"
                          class="link-underline link-underline-opacity-0">
                        {{ problem.collection.name }}/{{ problem.category.name }}
                        -
                        {{ problem.statuspage.statuspage.name }}
                      </router-link>
                    </small>
                  </b-card>
                </Slide>
                <template #addons>
                  <Navigation/>
                  <Pagination/>
                </template>
              </Carousel>
            </b-list-group-item>

            <b-list-group
                v-for="(collection, collectionIndex) in statuspagegroupview.statuspagegroup?.statuspagegroup_collections"
                :key="collectionIndex" class="mt-2">
              <b-list-group-item>
                <b-row>
                  <b-col>
                    {{ $t('Collection:') }}
                  </b-col>
                  <b-col>
                    <small class="float-end">
                      <statuspagegroups-icon :status="collection.cumulatedCollectionState"/>
                      {{ collection.name }}
                    </small>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col>
                    {{ $t('Categories:') }}
                  </b-col>
                </b-row>
                <b-row
                    v-for="(category, categoryIndex) in statuspagegroupview.statuspagegroup.statuspagegroup_categories"
                    :key="categoryIndex">
                  <b-col class="col-6">
                    <small>
                      <statuspagegroups-icon :status="category.cumulatedCategoryState"/>
                      {{ category.name }}
                    </small>
                  </b-col>
                  <b-col v-if="statuspagegroupview.matrix[collectionIndex][categoryIndex].total_statuspages > 0"
                         class="col-6">
                    <small class="float-end">
                      <status-text
                          :status="statuspagegroupview.matrix[collectionIndex][categoryIndex].cumulatedState"/>
                      <span v-if="statuspagegroupview.matrix[collectionIndex][categoryIndex].cumulatedState === 1"
                            class="ms-1 badge bg-warning">
                        {{ statuspagegroupview.matrix[collectionIndex][categoryIndex].total_warning }}
                      </span>
                      <span v-if="statuspagegroupview.matrix[collectionIndex][categoryIndex].cumulatedState === 2"
                            class="ms-1 badge bg-critical">
                        {{ statuspagegroupview.matrix[collectionIndex][categoryIndex].total_critical }}
                      </span>
                      <span v-if="statuspagegroupview.matrix[collectionIndex][categoryIndex].cumulatedState === 3"
                            class="ms-1 badge bg-unknown">
                        {{ statuspagegroupview.matrix[collectionIndex][categoryIndex].total_unknown }}
                      </span>
                      <span
                          v-if="statuspagegroupview.matrix[collectionIndex][categoryIndex].cumulatedState === -1 && statuspagegroupview.matrix[collectionIndex][categoryIndex].total_not_monitored > 0"
                          class="ms-1 badge bg-primary">
                        {{ statuspagegroupview.matrix[collectionIndex][categoryIndex].total_not_monitored }}
                      </span>
                    </small>
                  </b-col>

                </b-row>
              </b-list-group-item>

            </b-list-group>
          </b-list-group>
        </b-list-group-item>
      </div>
      <div v-show="selectedTab === 'tab2'" class="m-2">
        <b-card>
          <div class="row">
            <div class="col">
              <b-button v-b-toggle.filter class="m-0 w-100" variant="primary">
                <i class="fa-solid fa-filter"></i>
                {{ $t('Filter') }}
              </b-button>
            </div>
          </div>
          <b-collapse id="filter" class="mt-2">

            <div>
              <BFormCheckbox id="checkbox-1"
                             v-model="filter.state_1"
                             class="bg-warning"
                             name="checkbox-1"
                             size="lg"
                             switch
                             unchecked-value=false
                             value=true
              >
                <status-text :status="1"></status-text>
              </BFormCheckbox>
            </div>
            <div>
              <BFormCheckbox id="checkbox-2"
                             v-model="filter.state_2"
                             class="bg-critical"
                             name="checkbox-2"
                             size="lg"
                             switch
                             unchecked-value=false
                             value=true
              >
                <status-text :status="2"></status-text>
              </BFormCheckbox>
            </div>
            <div>
              <BFormCheckbox id="checkbox-3"
                             v-model="filter.state_3"
                             class="bg-secondary"
                             name="checkbox-3"
                             size="lg"
                             switch
                             unchecked-value=false
                             value=true
              >
                <status-text :status="3"></status-text>
              </BFormCheckbox>
            </div>
            <div class="d-flex w-100 mt-2">
              <b-form-input v-model="filter.collection" :placeholder="$t('Filter by collection')"></b-form-input>
            </div>
            <div class="d-flex w-100 mt-2">
              <b-form-input v-model="filter.category" :placeholder="$t('Filter by category')"></b-form-input>
            </div>
            <div class="d-flex w-100 mt-2">
              <b-form-input v-model="filter.statuspage_name" :placeholder="$t('Filter by statuspage')"></b-form-input>
            </div>

          </b-collapse>
        </b-card>

        <b-list-group v-for="problem in problemsFiltered" class="mt-2">
          <b-list-group-item>
            <b-row>
              <b-col class="col-6">
                {{ $t('Collection:') }}
              </b-col>
              <b-col class="col-6">
                <small class="float-end">
                  <statuspagegroups-icon :status="problem.cumulatedState"/>
                  {{ problem.collection.name }}
                </small>
              </b-col>
            </b-row>
            <b-row>
              <b-col class="col-6">
                {{ $t('Category:') }}
              </b-col>
              <b-col class="col-6">
                <small class="float-end">
                  {{ problem.category.name }}
                </small>
              </b-col>
            </b-row>
            <b-row>
              <b-col>
                {{ $t('Statuspage:') }}
              </b-col>
            </b-row>
            <b-row>
              <b-col class="col-6">
                <small>
                  <router-link
                      :to="{name: 'statuspagesDetails', params: {id: problem.statuspage.statuspage.id}}"
                      class="link-underline link-underline-opacity-0">
                    {{ problem.statuspage.statuspage.name }}
                  </router-link>
                </small>
              </b-col>
              <b-col class="col-6">
                <status-text :status="problem.cumulatedState" class="float-end"/>
              </b-col>
            </b-row>
          </b-list-group-item>
        </b-list-group>
      </div>

    </b-container>
  </div>
</template>
<script>
import { useToast } from "vue-toastification";
import { debounce } from "../../helpers";
import { BCardHeader, BContainer, BFormCheckbox, BListGroup, BListGroupItem } from 'bootstrap-vue-next';
import StatuspagegroupsIcon from './StatuspagegroupsIcon.vue';
import StatusText from './StatusText.vue';
import 'vue3-carousel/carousel.css';
import { Carousel, Slide, Pagination, Navigation } from 'vue3-carousel'

export default {
  name: "StatuspagegroupsDetails",
  components: {
    BFormCheckbox,
    BCardHeader,
    BListGroupItem, BListGroup, BContainer, StatuspagegroupsIcon, StatusText, Carousel, Slide, Pagination, Navigation
  },
  data() {
    return {
      loading: true,
      requestStatus: false,
      selectedTab: 'tab1',
      statuspagegroupview: {},
      mainHeaderBgClasses: '',
      mainHeaderRefreshBtnClasses: '',
      filter: {
        state_1: "true",
        state_2: "true",
        state_3: "true",
        collection: '',
        category: '',
        statuspage_name: ''
      },
      problemsFiltered: []
    }
  },
  async mounted() {
    await this.getData();
  },
  methods: {
    goBack: function() {
      this.$router.back();
    },
    setTab: function(val) {
      this.selectedTab = val;
    },

    refreshData() {
      this.getData(1);
    },
    getData: function() {
      let loader = this.$loading.show();
      this.loading = true;
      axios.post('/statuspagegroups/details.json', null, {
        params: {
          id: this.$route.params.id
        }
      })
          .then(response => {
            this.requestStatus = true;

            this.statuspagegroupview = response.data.statuspagegroupview ?? {};
            this.mainHeaderBgClasses = this.getHeaderBgClass();
            this.mainHeaderRefreshBtnClasses = this.getHeaderRefreshBtnClass();
            this.applyFilter();
          })
          .catch(error => {
            console.log(error);
            useToast().error(this.$t('error'), {
              timeout: 2000
            });

          })
          .finally(() => {
            loader.hide();
            this.loading = false;
          });
    },

    applyFilter: function() {
      if(!this.statuspagegroupview) {
        return;
      }
      let problemsFiltered = [];
      let stateFilter = [];

      if(this.filter.state_1 === "true") {
        stateFilter.push(1);
      }
      if(this.filter.state_2 === "true") {
        stateFilter.push(2);
      }
      if(this.filter.state_3 === "true") {
        stateFilter.push(3);
      }
      for(const problem of this.statuspagegroupview.problems) {

        if(stateFilter.length > 0 && !stateFilter.includes(problem.cumulatedState)) {
          continue;
        }

        if(this.filter.statuspage_name !== '') {
          if(!problem.statuspage.statuspage.name.toLowerCase().includes(this.filter.statuspage_name.toLowerCase())) {
            continue;
          }
        }

        if(this.filter.collection !== '') {
          if(!problem.collection.name.toLowerCase().includes(this.filter.collection.toLowerCase())) {
            continue;
          }
        }

        if(this.filter.category !== '') {
          if(!problem.category.name.toLowerCase().includes(this.filter.category.toLowerCase())) {
            continue;
          }
        }

        problemsFiltered.push(problem);
      }
      this.problemsFiltered = problemsFiltered;

    },

    getHeaderBgClass() {
      switch(this.statuspagegroupview.cumulatedStategroupState) {
        case 0:
        case "up":
        case "ok":
          return 'bg-ok shadow-lg-ok text-white';

        case 1:
        case "warning":
          return 'bg-warning shadow-lg-warning text-dark';

        case 2:
        case "down":
        case "critical":
          return 'bg-critical shadow-lg-critical text-white';

        case 3:
        case "unreachable":
        case "unknown":
          return 'bg-unknown shadow-lg-unknown text-white';

        default:
          // Not in monitoring
          return 'bg-primary shadow-lg-primary text-white';
      }
    },

    getHeaderRefreshBtnClass() {
      switch(this.statuspagegroupview.cumulatedStategroupState) {
        case 0:
        case "up":
        case "ok":
          return 'btn-success';
        case 1:
        case "warning":
          return 'btn-warning';

        case 2:
        case "down":
        case "critical":
          return 'btn-danger';

        case 3:
        case "unreachable":
        case "unknown":
          return 'btn-secundary text-white';

        default:
          return 'btn-primary';
      }
    }
  },
  watch: {
    filter: {
      handler(newValue, oldValue) {
        this.applyFilter();
      },
      deep: true
    }
  }
}
</script>


<style scoped>
.bg-primary, .not-monitored {
  background-color: #5856d6 !important;
}


.min-h-90 {
  min-height: 90px;
}

.status-circle {
  width: 15px;
  height: 15px;
  padding: 0;
  border-radius: 50%;
}

.flex-right {
  margin-left: auto;
}

.text-small {
  font-size: 0.875em;
}

</style>
